// src/components/auto/AutoForm.jsx - Mobile-optimized with native selects
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion } from "framer-motion";
import { Calendar, Car, FileText, Gauge, MapPin, Save, User, X } from "lucide-react";
import { useEffect, useState } from "react";

export default function AutoForm({
  auto,
  clienti,
  tettoie,
  sezioni,
  campate,
  stati,
  onSubmit,
  onCancel,
  isLoading,
  onCheckNumeroTelaio,
  isMobile = false // Add isMobile prop
}) {
  const [formData, setFormData] = useState({
    targa: "",
    marca: "",
    modello: "",
    numero_telaio: "",
    km: 0,
    data_ingresso: "",
    data_uscita: "",
    cliente_id: "",
    campata_id: "",
    stato_id: "",
    note: ""
  });

  const [selectedTettoia, setSelectedTettoia] = useState("");
  const [selectedSezione, setSelectedSezione] = useState("");
  const [errors, setErrors] = useState({});

  // Generate unique code for "senzatarga"
  const generateSenzaTargaCode = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    const hash = (timestamp + random).split('').reduce((a, b) => {
      a = ((a << 5) - a) + b.charCodeAt(0);
      return a & a;
    }, 0);
    const code = Math.abs(hash).toString(36).substring(0, 6).toUpperCase();
    return `ST-${code}`;
  };

  // Transform input to uppercase and filter allowed characters
  const transformAlphanumericInput = (value) => {
    return value.toUpperCase().replace(/[^A-Z0-9]/g, '');
  };

  // Handle special input for Targa field
  const handleTargaInput = (value) => {
    const cleanValue = value.toLowerCase().replace(/\s/g, '');

    // Check for "senzatarga" or "senza targa" triggers
    if (cleanValue === 'senzatarga' || cleanValue === 'senzatarga') {
      return generateSenzaTargaCode();
    }

    // Apply normal transformation
    return transformAlphanumericInput(value);
  };

  // Format date for input (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      return date.toISOString().split('T')[0];
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  // Format date for database (ISO string)
  const formatDateForDB = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      return date.toISOString();
    } catch (error) {
      console.error("Error formatting date for DB:", error);
      return null;
    }
  };

  // Enhanced function to check if a status requires location fields
  const isLocationRequired = (statoId) => {
    const stato = stati.find(s => s.id === statoId);
    if (!stato) return true; // Default to required if stato not found

    const statoName = stato.nome.toLowerCase();
    // Location is NOT required for these statuses (case insensitive)
    const nonLocationStatuses = ['uscita', 'manutenzione', 'chiusa', 'archiviata', 'eliminata'];

    return !nonLocationStatuses.some(status => statoName.includes(status));
  };

  // Reset Data Uscita function
  const resetDataUscita = () => {
    handleInputChange("data_uscita", "");
  };

  // Native Select Component for mobile
  const NativeSelect = ({ value, onChange, options, placeholder, disabled = false, error = false, children }) => {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={`w-full p-2 border rounded-md text-base bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-100 disabled:text-slate-500 ${error ? "border-red-500" : "border-slate-300"
          }`}
        style={{ minHeight: '44px' }} // iOS touch target size
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    );
  };

  // Prepare options for native selects
  const clientiOptions = clienti.map(cliente => ({
    id: cliente.id,
    name: `${cliente.nome} ${cliente.cognome}`.trim()
  }));

  const statiOptions = stati.map(stato => ({
    id: stato.id,
    name: stato.nome
  }));

  const tettoieOptions = tettoie.map(tettoia => ({
    id: tettoia.id,
    name: tettoia.nome
  }));

  const sezioniOptions = selectedTettoia
    ? sezioni.filter(s => s.tettoia_id === selectedTettoia).map(sezione => ({
      id: sezione.id,
      name: sezione.nome
    }))
    : [];

  const campateOptions = selectedSezione
    ? campate.filter(c => c.sezione_id === selectedSezione).map((campata, index) => ({
      id: campata.id,
      name: `${campata.nome} - ${tettoie.find(t => t.id === selectedTettoia)?.nome} / ${sezioni.find(s => s.id === selectedSezione)?.nome} (#${index + 1})`
    }))
    : [];

  useEffect(() => {
    if (auto) {
      setFormData({
        targa: auto.targa || "",
        marca: auto.marca || "",
        modello: auto.modello || "",
        numero_telaio: auto.numero_telaio || "",
        km: auto.km ?? 0,
        data_ingresso: formatDateForInput(auto.data_ingresso),
        data_uscita: formatDateForInput(auto.data_uscita),
        cliente_id: auto.cliente_id || "",
        campata_id: auto.campata_id || "",
        stato_id: auto.stato_id || "",
        note: auto.note || ""
      });

      // Set tettoia and sezione for editing only if location is required
      if (auto.campata_id && isLocationRequired(auto.stato_id)) {
        const campata = campate.find(c => c.id === auto.campata_id);
        if (campata) {
          const sezione = sezioni.find(s => s.id === campata.sezione_id);
          if (sezione) {
            setSelectedSezione(sezione.id);
            const tettoia = tettoie.find(t => t.id === sezione.tettoia_id);
            if (tettoia) {
              setSelectedTettoia(tettoia.id);
            }
          }
        }
      } else {
        // Clear location fields if status doesn't require them
        setSelectedTettoia("");
        setSelectedSezione("");
      }
    } else {
      // Reset form for new auto
      setFormData({
        targa: "",
        marca: "",
        modello: "",
        numero_telaio: "",
        km: 0,
        data_ingresso: "",
        data_uscita: "",
        cliente_id: "",
        campata_id: "",
        stato_id: "",
        note: ""
      });
      setSelectedTettoia("");
      setSelectedSezione("");
    }
    setErrors({});
  }, [auto, campate, sezioni, tettoie, stati]);

  const validateForm = async () => {
    const newErrors = {};

    // Basic required fields
    if (!formData.targa.trim()) {
      newErrors.targa = "La targa è obbligatoria";
    }
    if (!formData.marca.trim()) {
      newErrors.marca = "La marca è obbligatoria";
    }
    if (!formData.modello.trim()) {
      newErrors.modello = "Il modello è obbligatorio";
    }
    if (!formData.data_ingresso) {
      newErrors.data_ingresso = "La data di ingresso è obbligatoria";
    }
    if (!formData.cliente_id) {
      newErrors.cliente_id = "Il cliente è obbligatorio";
    }
    if (!formData.stato_id) {
      newErrors.stato_id = "Lo stato è obbligatorio";
    }

    // Check numero_telaio for duplicates if provided
    if (formData.numero_telaio.trim() && onCheckNumeroTelaio) {
      try {
        const duplicateCheck = await onCheckNumeroTelaio(
          formData.numero_telaio.trim(),
          auto?.id // Exclude current auto when editing
        );

        if (duplicateCheck.isDuplicate && duplicateCheck.existingAuto) {
          const existing = duplicateCheck.existingAuto;
          newErrors.numero_telaio = `Questo numero di telaio è già utilizzato dall'auto con targa "${existing.targa}" (${existing.marca} ${existing.modello})`;
        }
      } catch (error) {
        console.error("Error checking numero_telaio duplicate:", error);
        // Don't block submission for API errors, but log them
      }
    }

    // Location fields validation (only if status requires them)
    if (formData.stato_id && isLocationRequired(formData.stato_id)) {
      if (!selectedTettoia) {
        newErrors.selectedTettoia = "La tettoia è obbligatoria per questo stato";
      }
      if (!selectedSezione) {
        newErrors.selectedSezione = "La sezione è obbligatoria per questo stato";
      }
      if (!formData.campata_id) {
        newErrors.campata_id = "La campata è obbligatoria per questo stato";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = await validateForm();

    if (isValid) {
      try {
        // Format dates for database
        const dataToSubmit = {
          ...formData,
          data_ingresso: formatDateForDB(formData.data_ingresso),
          data_uscita: formData.data_uscita ? formatDateForDB(formData.data_uscita) : null
        };

        // Clear location fields if status doesn't require them
        if (!isLocationRequired(formData.stato_id)) {
          dataToSubmit.campata_id = null;
        }

        await onSubmit(dataToSubmit);
      } catch (error) {
        console.error("Form submission error:", error);
        // Let the parent component handle the error display
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: "" }));
    }
  };

  // Special handler for Targa field
  const handleTargaChange = (e) => {
    const transformedValue = handleTargaInput(e.target.value);
    handleInputChange("targa", transformedValue);
  };

  // Special handler for Numero Telaio field
  const handleNumeroTelaioChange = (e) => {
    const transformedValue = transformAlphanumericInput(e.target.value);
    handleInputChange("numero_telaio", transformedValue);
  };

  // Special handler for Km field (numeric only, defaults to 0)
  const handleKmChange = (e) => {
    const digitsOnly = e.target.value.replace(/[^0-9]/g, '');
    handleInputChange("km", digitsOnly === '' ? 0 : parseInt(digitsOnly, 10));
  };

  // Handle status change - clear location if status doesn't require it
  const handleStatusChange = (statoId) => {
    handleInputChange("stato_id", statoId);

    // If the new status doesn't require location, clear all location fields
    if (!isLocationRequired(statoId)) {
      setSelectedTettoia("");
      setSelectedSezione("");
      setFormData(prev => ({ ...prev, campata_id: "" }));

      // Clear location-related errors
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.selectedTettoia;
        delete newErrors.selectedSezione;
        delete newErrors.campata_id;
        return newErrors;
      });
    }
  };

  // Filter sezioni based on selected tettoia
  const filteredSezioni = selectedTettoia
    ? sezioni.filter(s => s.tettoia_id === selectedTettoia)
    : [];

  // Filter campate based on selected sezione
  const filteredCampate = selectedSezione
    ? campate.filter(c => c.sezione_id === selectedSezione)
    : [];

  // Check if location fields should be shown/required
  const locationRequired = formData.stato_id ? isLocationRequired(formData.stato_id) : true;

  // Get status info message
  const getStatusInfoMessage = () => {
    if (!formData.stato_id) return null;

    const stato = stati.find(s => s.id === formData.stato_id);
    if (!stato) return null;

    const statoName = stato.nome.toLowerCase();

    if (statoName.includes('chiusa') || statoName.includes('archiviata')) {
      return {
        type: 'warning',
        message: 'Questo stato archivierà automaticamente l\'auto. La posizione non è necessaria.'
      };
    }

    if (statoName.includes('uscita')) {
      return {
        type: 'info',
        message: 'Auto in uscita - la posizione non è necessaria.'
      };
    }

    if (statoName.includes('manutenzione')) {
      return {
        type: 'info',
        message: 'Auto in manutenzione - la posizione non è necessaria.'
      };
    }

    return null;
  };

  const statusInfo = getStatusInfoMessage();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden"
    >
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Car className="w-6 h-6 text-white" />
            <h2 className="text-xl font-semibold text-white">
              {auto ? "Modifica Auto" : "Nuova Auto"}
            </h2>
          </div>
          <Button
            type="button"
            onClick={onCancel}
            variant="ghost"
            size="sm"
            className="text-white hover:bg-white/20"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className={`${isMobile ? 'p-4' : 'p-6'}`}>
        <div className={`grid ${isMobile ? 'grid-cols-1 gap-4' : 'md:grid-cols-2 gap-6'}`}>
          {/* Targa */}
          <div className="space-y-2">
            <Label htmlFor="targa" className="flex items-center space-x-2">
              <FileText className="w-4 h-4" />
              <span>Targa *</span>
            </Label>
            <Input
              id="targa"
              value={formData.targa}
              onChange={handleTargaChange}
              placeholder="Es: AB123CD o scrivi 'senzatarga'"
              className={`${errors.targa ? "border-red-500" : ""} ${isMobile ? 'text-base' : ''}`}
              style={isMobile ? { minHeight: '44px' } : {}}
            />
            {errors.targa && (
              <p className="text-sm text-red-500">{errors.targa}</p>
            )}
            <p className={`${isMobile ? 'text-xs' : 'text-xs'} text-slate-500`}>
              Solo lettere e numeri. Scrivi "senzatarga" per generare un codice automatico.
            </p>
          </div>

          {/* Marca */}
          <div className="space-y-2">
            <Label htmlFor="marca" className="flex items-center space-x-2">
              <Car className="w-4 h-4" />
              <span>Marca *</span>
            </Label>
            <Input
              id="marca"
              value={formData.marca}
              onChange={(e) => handleInputChange("marca", e.target.value)}
              placeholder="Es: Fiat"
              className={`${errors.marca ? "border-red-500" : ""} ${isMobile ? 'text-base' : ''}`}
              style={isMobile ? { minHeight: '44px' } : {}}
            />
            {errors.marca && (
              <p className="text-sm text-red-500">{errors.marca}</p>
            )}
          </div>

          {/* Modello */}
          <div className="space-y-2">
            <Label htmlFor="modello" className="flex items-center space-x-2">
              <Car className="w-4 h-4" />
              <span>Modello *</span>
            </Label>
            <Input
              id="modello"
              value={formData.modello}
              onChange={(e) => handleInputChange("modello", e.target.value)}
              placeholder="Es: Punto"
              className={`${errors.modello ? "border-red-500" : ""} ${isMobile ? 'text-base' : ''}`}
              style={isMobile ? { minHeight: '44px' } : {}}
            />
            {errors.modello && (
              <p className="text-sm text-red-500">{errors.modello}</p>
            )}
          </div>

          {/* Numero Telaio */}
          <div className="space-y-2">
            <Label htmlFor="numero_telaio" className="flex items-center space-x-2">
              <FileText className="w-4 h-4" />
              <span>Numero Telaio</span>
            </Label>
            <Input
              id="numero_telaio"
              value={formData.numero_telaio}
              onChange={handleNumeroTelaioChange}
              placeholder="Es: VIN123456789"
              className={`${errors.numero_telaio ? "border-red-500" : ""} ${isMobile ? 'text-base' : ''}`}
              style={isMobile ? { minHeight: '44px' } : {}}
            />
            {errors.numero_telaio && (
              <p className="text-sm text-red-500">{errors.numero_telaio}</p>
            )}
            <p className={`${isMobile ? 'text-xs' : 'text-xs'} text-slate-500`}>
              Solo lettere e numeri. Campo opzionale - sarà verificata la duplicazione.
            </p>
          </div>

          {/* Km */}
          <div className="space-y-2">
            <Label htmlFor="km" className="flex items-center space-x-2">
              <Gauge className="w-4 h-4" />
              <span>Km</span>
            </Label>
            <Input
              id="km"
              type="text"
              inputMode="numeric"
              value={formData.km}
              onChange={handleKmChange}
              placeholder="0"
              className={`${errors.km ? "border-red-500" : ""} ${isMobile ? 'text-base' : ''}`}
              style={isMobile ? { minHeight: '44px' } : {}}
            />
            {errors.km && (
              <p className="text-sm text-red-500">{errors.km}</p>
            )}
            <p className={`${isMobile ? 'text-xs' : 'text-xs'} text-slate-500`}>
              Campo opzionale - valore predefinito 0.
            </p>
          </div>

          {/* Spacer to keep Km alone on its row (desktop only) */}
          <div className="hidden md:block" aria-hidden="true" />

          {/* Data Ingresso */}
          <div className="space-y-2">
            <Label htmlFor="data_ingresso" className="flex items-center space-x-2">
              <Calendar className="w-4 h-4" />
              <span>Data Ingresso *</span>
            </Label>
            <Input
              id="data_ingresso"
              type="date"
              value={formData.data_ingresso}
              onChange={(e) => handleInputChange("data_ingresso", e.target.value)}
              className={`${errors.data_ingresso ? "border-red-500" : ""} ${isMobile ? 'text-base' : ''}`}
              style={isMobile ? { minHeight: '44px' } : {}}
            />
            {errors.data_ingresso && (
              <p className="text-sm text-red-500">{errors.data_ingresso}</p>
            )}
          </div>

          {/* Data Uscita */}
          <div className="space-y-2">
            <Label htmlFor="data_uscita" className="flex items-center space-x-2">
              <Calendar className="w-4 h-4" />
              <span>Data Uscita (opzionale)</span>
            </Label>

            <div className="relative">
              <Input
                id="data_uscita"
                type="date"
                value={formData.data_uscita}
                onChange={(e) => handleInputChange("data_uscita", e.target.value)}
                placeholder="Lascia vuoto se l'auto è ancora presente"
                className={`${formData.data_uscita ? 'pr-20' : ''} ${isMobile ? 'text-base' : ''}`}
                style={isMobile ? { minHeight: '44px' } : {}}
              />

              {/* Reset button */}
              {formData.data_uscita && (
                <div className="absolute right-0 top-0 h-full flex items-center">
                  <Button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      resetDataUscita();
                    }}
                    variant="ghost"
                    size="sm"
                    className="h-8 w-16 mr-1 text-xs text-slate-400 hover:text-red-500 hover:bg-red-50 border border-slate-200 bg-white shadow-sm"
                    title="Cancella data uscita"
                    style={isMobile ? { touchAction: 'manipulation' } : {}}
                  >
                    <X className="w-3 h-3 mr-1" />
                    Reset
                  </Button>
                </div>
              )}
            </div>

            {/* Mobile reset button */}
            {formData.data_uscita && isMobile && (
              <Button
                type="button"
                onClick={resetDataUscita}
                variant="outline"
                size="sm"
                className="w-full text-xs text-slate-600 hover:text-red-600 hover:bg-red-50 border-slate-200"
                style={{ touchAction: 'manipulation' }}
              >
                <X className="w-3 h-3 mr-2" />
                Cancella Data Uscita
              </Button>
            )}
          </div>

          {/* Cliente - Mobile vs Desktop */}
          <div className="space-y-2">
            <Label className="flex items-center space-x-2">
              <User className="w-4 h-4" />
              <span>Cliente *</span>
            </Label>

            {isMobile ? (
              <NativeSelect
                value={formData.cliente_id}
                onChange={(value) => handleInputChange("cliente_id", value)}
                options={clientiOptions}
                placeholder="Seleziona cliente"
                error={!!errors.cliente_id}
              />
            ) : (
              <Select
                value={formData.cliente_id}
                onValueChange={(value) => handleInputChange("cliente_id", value)}
              >
                <SelectTrigger className={errors.cliente_id ? "border-red-500" : ""}>
                  <SelectValue placeholder="Seleziona cliente" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px] overflow-y-auto">
                  {clienti.map((cliente) => (
                    <SelectItem key={cliente.id} value={cliente.id}>
                      {cliente.nome} {cliente.cognome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {errors.cliente_id && (
              <p className="text-sm text-red-500">{errors.cliente_id}</p>
            )}
          </div>

          {/* Stato - Mobile vs Desktop */}
          <div className="space-y-2">
            <Label className="flex items-center space-x-2">
              <FileText className="w-4 h-4" />
              <span>Stato *</span>
            </Label>

            {isMobile ? (
              <NativeSelect
                value={formData.stato_id}
                onChange={handleStatusChange}
                options={statiOptions}
                placeholder="Seleziona stato"
                error={!!errors.stato_id}
              />
            ) : (
              <Select
                value={formData.stato_id}
                onValueChange={handleStatusChange}
              >
                <SelectTrigger className={errors.stato_id ? "border-red-500" : ""}>
                  <SelectValue placeholder="Seleziona stato" />
                </SelectTrigger>
                <SelectContent className="max-h-[200px] overflow-y-auto">
                  {stati.map((stato) => (
                    <SelectItem key={stato.id} value={stato.id}>
                      {stato.nome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            {errors.stato_id && (
              <p className="text-sm text-red-500">{errors.stato_id}</p>
            )}
          </div>

          {/* Status info message */}
          {statusInfo && (
            <div className={`${isMobile ? 'col-span-1' : 'md:col-span-2'} p-4 rounded-lg border ${statusInfo.type === 'warning'
                ? 'bg-amber-50 border-amber-200'
                : 'bg-blue-50 border-blue-200'
              }`}>
              <p className={`text-sm ${statusInfo.type === 'warning'
                  ? 'text-amber-700'
                  : 'text-blue-700'
                }`}>
                <span className="font-medium">
                  {statusInfo.type === 'warning' ? 'Attenzione:' : 'Info:'}
                </span> {statusInfo.message}
              </p>
            </div>
          )}

          {/* Location fields - only show if required by status */}
          {locationRequired && (
            <>
              {/* Tettoia - Mobile vs Desktop */}
              <div className="space-y-2">
                <Label className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>Tettoia *</span>
                </Label>

                {isMobile ? (
                  <NativeSelect
                    value={selectedTettoia}
                    onChange={(value) => {
                      setSelectedTettoia(value);
                      setSelectedSezione("");
                      setFormData(prev => ({ ...prev, campata_id: "" }));
                      if (errors.selectedTettoia) {
                        setErrors(prev => ({ ...prev, selectedTettoia: "" }));
                      }
                    }}
                    options={tettoieOptions}
                    placeholder="Seleziona tettoia"
                    error={!!errors.selectedTettoia}
                  />
                ) : (
                  <Select
                    value={selectedTettoia}
                    onValueChange={(value) => {
                      setSelectedTettoia(value);
                      setSelectedSezione("");
                      setFormData(prev => ({ ...prev, campata_id: "" }));
                      if (errors.selectedTettoia) {
                        setErrors(prev => ({ ...prev, selectedTettoia: "" }));
                      }
                    }}
                  >
                    <SelectTrigger className={errors.selectedTettoia ? "border-red-500" : ""}>
                      <SelectValue placeholder="Seleziona tettoia" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[200px] overflow-y-auto">
                      {tettoie.map((tettoia) => (
                        <SelectItem key={tettoia.id} value={tettoia.id}>
                          {tettoia.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {errors.selectedTettoia && (
                  <p className="text-sm text-red-500">{errors.selectedTettoia}</p>
                )}
              </div>

              {/* Sezione - Mobile vs Desktop */}
              <div className="space-y-2">
                <Label className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>Sezione *</span>
                </Label>

                {isMobile ? (
                  <NativeSelect
                    value={selectedSezione}
                    onChange={(value) => {
                      setSelectedSezione(value);
                      setFormData(prev => ({ ...prev, campata_id: "" }));
                      if (errors.selectedSezione) {
                        setErrors(prev => ({ ...prev, selectedSezione: "" }));
                      }
                    }}
                    options={sezioniOptions}
                    placeholder={selectedTettoia ? "Seleziona sezione" : "Prima seleziona una tettoia"}
                    disabled={!selectedTettoia}
                    error={!!errors.selectedSezione}
                  />
                ) : (
                  <Select
                    value={selectedSezione}
                    onValueChange={(value) => {
                      setSelectedSezione(value);
                      setFormData(prev => ({ ...prev, campata_id: "" }));
                      if (errors.selectedSezione) {
                        setErrors(prev => ({ ...prev, selectedSezione: "" }));
                      }
                    }}
                    disabled={!selectedTettoia}
                  >
                    <SelectTrigger className={errors.selectedSezione ? "border-red-500" : ""}>
                      <SelectValue placeholder={selectedTettoia ? "Seleziona sezione" : "Prima seleziona una tettoia"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-[200px] overflow-y-auto">
                      {filteredSezioni.map((sezione) => (
                        <SelectItem key={sezione.id} value={sezione.id}>
                          {sezione.nome}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {errors.selectedSezione && (
                  <p className="text-sm text-red-500">{errors.selectedSezione}</p>
                )}
              </div>

              {/* Campata - Mobile vs Desktop */}
              <div className={`space-y-2 ${isMobile ? 'col-span-1' : 'md:col-span-2'}`}>
                <Label className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>Campata * ({filteredCampate.length} disponibili)</span>
                </Label>

                {isMobile ? (
                  <NativeSelect
                    value={formData.campata_id}
                    onChange={(value) => {
                      handleInputChange("campata_id", value);
                      if (errors.campata_id) {
                        setErrors(prev => ({ ...prev, campata_id: "" }));
                      }
                    }}
                    options={campateOptions}
                    placeholder={selectedSezione ? "Seleziona campata" : "Prima seleziona una sezione"}
                    disabled={!selectedSezione}
                    error={!!errors.campata_id}
                  />
                ) : (
                  <div className="relative">
                    <select
                      value={formData.campata_id}
                      onChange={(e) => {
                        handleInputChange("campata_id", e.target.value);
                        if (errors.campata_id) {
                          setErrors(prev => ({ ...prev, campata_id: "" }));
                        }
                      }}
                      disabled={!selectedSezione}
                      className={`w-full h-10 px-3 py-2 bg-white border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-100 disabled:text-slate-500 ${errors.campata_id ? "border-red-500" : "border-slate-300"
                        }`}
                      size="1"
                    >
                      <option value="">
                        {selectedSezione ? "Seleziona campata" : "Prima seleziona una sezione"}
                      </option>
                      {filteredCampate.map((campata, index) => (
                        <option key={campata.id} value={campata.id}>
                          {campata.nome} - {tettoie.find(t => t.id === selectedTettoia)?.nome} / {sezioni.find(s => s.id === selectedSezione)?.nome} (#{index + 1})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {errors.campata_id && (
                  <p className="text-sm text-red-500">{errors.campata_id}</p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Note */}
        <div className={`space-y-2 ${isMobile ? 'mt-4' : 'mt-6'}`}>
          <Label htmlFor="note" className="flex items-center space-x-2">
            <FileText className="w-4 h-4" />
            <span>Note (opzionale)</span>
          </Label>
          <textarea
            id="note"
            value={formData.note}
            onChange={(e) => handleInputChange("note", e.target.value)}
            placeholder="Inserisci eventuali note per questa auto..."
            rows={3}
            className={`w-full px-3 py-2 border border-slate-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical ${isMobile ? 'text-base' : ''}`}
            style={isMobile ? { minHeight: '80px' } : {}}
          />
        </div>

        <div className={`flex justify-end space-x-3 ${isMobile ? 'mt-6 pt-4' : 'mt-8 pt-6'} border-t border-slate-200`}>
          <Button
            type="button"
            onClick={onCancel}
            variant="outline"
            disabled={isLoading}
            className={isMobile ? 'flex-1' : ''}
            style={isMobile ? { touchAction: 'manipulation' } : {}}
          >
            Annulla
          </Button>
          <Button
            type="submit"
            disabled={isLoading}
            className={`bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 ${isMobile ? 'flex-1' : ''}`}
            style={isMobile ? { touchAction: 'manipulation' } : {}}
          >
            <Save className="w-4 h-4 mr-2" />
            {isLoading ? "Salvando..." : "Salva"}
          </Button>
        </div>
      </form>
    </motion.div>
  );
}